<template>
    <Modal id="payyo-configure-tips-modal"
           icon="fa-solid fa-coins"
           title="PAYYO_TIPS.MODAL_TITLE"
           :cancel="cancel"
           ok-label="SAVE"
           :ok="saveTips"
           :ok-enabled="validForm"
           :ok-spinner="saving">
        <!-- General errors -->
        <div v-if="errorMessage"
             class="alert alert-danger">
            <span class="fa-solid fa-exclamation-circle"></span> {{ errorMessage }}
        </div>

        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

        <div v-if="!errorMessage && !loading">
            <form class="form-h"
                  @submit.prevent="submit">
                <!-- Errors specifically to do with saving - probably a data issue. -->
                <div v-if="saveError"
                     class="alert alert-danger">
                    <span class="fa-solid fa-exclamation-circle"></span> {{ saveError }}
                </div>

                <div class="form-group">
                    <label class="control-label">{{ l('PAYYO_TIPS.TYPE') }}</label>
                    <div class="form-group__checkboxes">
                        <label class="form-group__checkbox">
                            <input v-model="tipConfig.mode"
                                   class="form-control"
                                   type="radio"
                                   value="percentage">{{ l('PAYYO_TIPS.TYPE_PERCENTAGE') }}</label>

                        <label class="form-group__checkbox">
                            <input v-model="tipConfig.mode"
                                   class="form-control"
                                   type="radio"
                                   value="fixed">{{ l('PAYYO_TIPS.TYPE_FIXED') }}</label>

                        <label class="form-group__checkbox">
                            <input v-model="tipConfig.mode"
                                   class="form-control"
                                   type="radio"
                                   value="smart">{{ l('PAYYO_TIPS.TYPE_SMART') }}</label>
                    </div>
                </div>

                <div v-if="tipConfig.mode === tipMode.SMART"
                     class="alert alert-info alert--small">
                    {{ l('PAYYO_TIPS.TYPE_SMART__ALERT') }}
                </div>

                <div v-if="tipConfig.mode === tipMode.SMART"
                     data-testid="tips-config-smart-section"
                     class="form-group">
                    <label class="control-label">
                        {{ l('PAYYO_TIPS.SMART_TIP_THRESHOLD') }}
                    </label>
                    <div class="form-h__right">
                        <div class="input-group-flex">
                            <div class="input-group input-group-lg input-group__wide"
                                 data-testid="tips-config-smart-wrapper"
                                 :class="{'has-error': !tipConfigValidity.smartTipThreshold}">
                                <span class="input-group-addon">{{ currencySymbol }}</span>
                                <input v-model="tipConfig.smartTipThreshold"
                                       data-testid="tips-config-smart"
                                       type="number"
                                       class="form-control">
                            </div>
                        </div>
                        <p class="help-block">
                            {{ l('PAYYO_TIPS.TYPE_SMART__INFO') }}
                        </p>
                    </div>
                </div>

                <div v-if="tipConfig.mode === tipMode.FIXED || tipConfig.mode === tipMode.SMART"
                     data-testid="tips-config-fixed-amounts-section"
                     class="form-group">
                    <label class="control-label">
                        {{ l('PAYYO_TIPS.FIXED_AMOUNTS') }}
                    </label>
                    <div class="form-h__right">
                        <div class="input-group-flex">
                            <!-- note index starts with 1 -->
                            <div v-for="i in 3"
                                 :key="('f-' + i)"
                                 :data-testid="'tips-config-fixed-amounts-wrapper-' + (i-1)"
                                 class="input-group input-group-lg input-group__wide"
                                 :class="{'has-error': !tipConfigValidity.fixedAmounts[i-1]}">
                                {{ tipConfigValidity.fixedAmounts[i-1].value }}
                                <span class="input-group-addon">£</span>
                                <input v-model="tipConfig.fixedAmounts[i-1]"
                                       :data-testid="'tips-config-fixed-amounts-' + (i-1)"
                                       type="number"
                                       step="0.5"
                                       class="form-control" />
                            </div>
                        </div>
                        <p class="help-block">
                            {{ l('PAYYO_TIPS.TYPE_FIXED__INFO') }}
                        </p>
                    </div>
                </div>

                <div v-if="tipConfig.mode === tipMode.PERCENTAGE || tipConfig.mode === tipMode.SMART"
                     data-testid="tips-config-percentages-section"
                     class="form-group">
                    <label class="control-label">
                        {{ l('PAYYO_TIPS.PERCENTAGES') }}
                    </label>
                    <div class="form-h__right">
                        <div class="input-group-flex">
                            <!-- note index starts with 1 -->
                            <div v-for="i in 3"
                                 :key="('p-' + i)"
                                 :data-testid="'tips-config-percentages-wrapper-' + (i-1)"
                                 class="input-group input-group-lg"
                                 :class="{'has-error': !tipConfigValidity.percentages[i-1]}">
                                <input v-model="tipConfig.percentages[i-1]"
                                       :data-testid="'tips-config-percentages-' + (i-1)"
                                       type="number"
                                       min="1"
                                       max="100"
                                       class="form-control" />
                                <span class="input-group-addon">%</span>
                            </div>
                        </div>
                        <p class="help-block">
                            {{ l('PAYYO_TIPS.TYPE_PERCENTAGE__INFO') }}
                        </p>
                    </div>
                </div>
            </form>

            <p class="text-center mt-1 small text-muted">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ l('PAYYO_TIPS.COMPANY_WIDE_ALERT') }}
            </p>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';

import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PayyoTipMode from '@/epos/vue/modals/Settings/Enums/PayyoTipMode';
import { container } from '@/container';
import { defineComponent } from 'vue';
import { formatMoney } from '@/lib/Formatting/currency';
import { fromMinorUnit, toMinorUnit } from '@/functions/math';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default defineComponent({
    name: 'PayyoConfigureTipsModal',

    components: {
        PageLoadingSpinner,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
    },

    data() {
        return {
            errorMessage: null,
            saveError: null,
            tipConfig: {
                /**
                 * fixed, percentage, or smart
                 *
                 * @see {PayyoTipMode}
                 *
                 * @type {string|null}
                 */
                mode: null,
                fixedAmounts: [
                    null,
                    null,
                    null,
                ],
                percentages: [
                    null,
                    null,
                    null,
                ],
                smartTipThreshold: 0,
            },
            tipConfigValidity: {
                fixedAmounts: [
                    false,
                    false,
                    false,
                ],
                percentages: [
                    false,
                    false,
                    false,
                ],
                smartTipThreshold: false,
            },
            validForm: false,
            tipMode: PayyoTipMode,
            saving: false,
            loading: true,
        };
    },

    computed: {
        /**
         * @return {string}
         */
        currencySymbol() {
            return container.currencyService.getSymbol();
        },
    },

    watch: {
        tipConfig: {
            handler() {
                this.validateForm();
            },
            deep: true,
        },
    },

    created() {
        try {
            container.payyoDriver.init(false);
            container.payyoDriver.getMerchant()
                .then((data) => {
                    const t = data.settings.tips_configuration;
                    if (t?.fixed_amounts) {
                        for (let i = 0; i < 3; i++) {
                            if (t.fixed_amounts[i]) {
                                this.tipConfig.fixedAmounts[i] = formatMoney(fromMinorUnit(t.fixed_amounts[i]));
                                this.tipConfigValidity.fixedAmounts[i] = true;
                            }
                        }
                    }
                    if (t?.percentages) {
                        for (let i = 0; i < 3; i++) {
                            if (t.percentages[i]) {
                                this.tipConfig.percentages[i] = t.percentages[i];
                                this.tipConfigValidity.percentages[i] = true;
                            }
                        }
                    }
                    if (t?.smart_tip_threshold) {
                        this.tipConfig.smartTipThreshold = formatMoney(fromMinorUnit(t.smart_tip_threshold));
                        this.tipConfigValidity.smartTipThreshold = true;
                    }

                    if (t?.smart_tip_threshold) {
                        this.tipConfig.mode = PayyoTipMode.SMART;
                    } else if (t?.fixed_amounts) {
                        this.tipConfig.mode = PayyoTipMode.FIXED;
                    } else {
                        this.tipConfig.mode = PayyoTipMode.PERCENTAGE;
                    }
                }).catch((error) => {
                    this.errorMessage = error.message;
                })
                .finally(() => {
                    this.loading = false;
                });
        } catch (error) {
            this.errorMessage = error.message;
            this.loading = false;
        }
    },

    methods: {
        async saveTips() {
            const confirmed = await container.dialogService.confirm(
                'PAYYO_TIPS.CONFIRM_SAVE_MESSAGE', {
                    cancelLabel: 'CANCEL',
                    okLabel: 'I_UNDERSTAND_CONTINUE',
                    cancelColor: 'default',
                },
            );
            if (!confirmed) {
                return;
            }

            this.saving = true;
            try {
                // eslint-disable-next-line camelcase
                let tips_config = {};

                this.tipConfig.fixedAmounts = this.tipConfig.fixedAmounts.filter(Boolean);
                this.tipConfig.percentages = this.tipConfig.percentages.filter(Boolean);

                if (this.tipConfig.mode === PayyoTipMode.FIXED || this.tipConfig.mode === PayyoTipMode.SMART) {
                    // eslint-disable-next-line camelcase
                    tips_config.fixed_amounts = this.tipConfig.fixedAmounts.map((num) => toMinorUnit(num));
                }
                if (this.tipConfig.mode === PayyoTipMode.PERCENTAGE || this.tipConfig.mode === PayyoTipMode.SMART) {
                    // eslint-disable-next-line camelcase
                    tips_config.percentages = this.tipConfig.percentages
                        .map((percentage) => parseInt(percentage))
                        .filter(Boolean);
                }
                if (this.tipConfig.mode === PayyoTipMode.SMART) {
                    // eslint-disable-next-line camelcase
                    tips_config.smart_tip_threshold = toMinorUnit(this.tipConfig.smartTipThreshold);
                }

                const newTipsConfig = {
                    // eslint-disable-next-line camelcase
                    tips_config,
                };

                container.payyoDriver.updateTipsConfig(newTipsConfig).then(() => {
                    container.toastFactory.successTop(
                        'PAYYO_TIPS.SUCCESS_TOAST_TITLE',
                    );
                    this.resolve();
                }).catch((error) => {
                    this.saving = false;
                    this.saveError = error.message;
                });
            } catch (error) {
                this.saving = false;
                this.errorMessage = error.message;
            }
        },

        validateForm() {
            // There are 3 valid tip configs:
            // - 3 x fixed amounts
            // - 3 x percentage amounts
            // - 3 x fixed amounts + 3 x percentage amounts + 1 x smart_tip_threshold
            this.validForm = false;

            // Each value must be a unique number > 1
            const validateFixed = () => {
                let validGroup = true;
                let values = [];
                for (let i = 0; i < 3; i++) {
                    this.tipConfig.fixedAmounts[i] = formatMoney(this.tipConfig.fixedAmounts[i]);
                    if (
                        !this.tipConfig.fixedAmounts[i]
                        || this.tipConfig.fixedAmounts[i] < 1
                        || values.includes(this.tipConfig.fixedAmounts[i])
                    ) {
                        this.tipConfigValidity.fixedAmounts[i] = false;
                        validGroup = false;
                        continue;
                    }
                    // We use formatMoney to ensure we are comparing like with like.
                    values.push(formatMoney(this.tipConfig.fixedAmounts[i]));
                    this.tipConfigValidity.fixedAmounts[i] = true;
                }
                return validGroup;
            };

            const validatePercentage = () => {
                let validGroup = true;
                let values = [];
                for (let i = 0; i < 3; i++) {
                    const percentage = this.tipConfig.percentages[i];
                    if (
                        !percentage
                        || percentage < 1 // Must be a number between 1 and 100
                        || percentage > 100
                        || parseInt(percentage) !== percentage // Must be an integer
                        || values.includes(percentage) // Must be unique
                    ) {
                        this.tipConfigValidity.percentages[i] = false;
                        validGroup = false;
                        continue;
                    }
                    values.push(this.tipConfig.percentages[i]);
                    this.tipConfigValidity.percentages[i] = true;
                }
                return validGroup;
            };

            const validateSmartTipThreshold = () => {
                if (!this.tipConfig.smartTipThreshold || this.tipConfig.smartTipThreshold < 0) {
                    this.tipConfigValidity.smartTipThreshold = false;
                    return false;
                }

                this.tipConfigValidity.smartTipThreshold = true;
                return true;
            };

            switch (this.tipConfig.mode) {
                case PayyoTipMode.FIXED:
                    this.validForm = validateFixed();
                    break;
                case PayyoTipMode.PERCENTAGE:
                    this.validForm = validatePercentage();
                    break;
                default: // PayyoTipMode.SMART
                    // Validate everything and don't exit early on the first fail.
                    this.validForm = [
                        validateFixed(),
                        validatePercentage(),
                        validateSmartTipThreshold(),
                    ].every((r) => r === true);
            }
        },
    },
});
</script>

<style lang="less" scoped>
@import "../../../../less/variables.less";

@media (min-width: @non-mobile-min) {
        .form-h .input-group-flex .form-control{
            width: 100%;
        }

        .input-group {
            width: 125px;
        }
}

</style>
